import {FC, PropsWithChildren, useMemo} from "react";
import {useMarketCategories, useMarketItems} from "@hooks/selectors/services.selectors";
import {injectContextArray} from "@components/constructor";
import {MarketCategory} from "@interfaces/user/market";
import {MarketCategoryProvider} from "./context";
import {InjectContextType} from "@interfaces/constructor/constructor";
import {useParams} from "react-router";
import {useItems} from "@hooks/selectors/user/item.selectors";
import {useGameServerAuto, useLoginServerAuto} from "@hooks/selectors/game.selectors";
import {checkItemServers} from "../../../../../../helpers/utils";

interface MarketCategoriesListProps {
    default_category?: string;
    filter_items_by_login?: boolean;
    filter_items_by_game?: boolean;
}

interface MarketCategoriesListChildProps {
    market_category_has_icon: boolean;
    market_category_active: boolean;
    market_category_id: string;
    market_category_url: string;
    market_category_loading: boolean;
    market_category_loaded: boolean;
    market_category_error: boolean;
    market_category_items_count: number;
}

const MarketCategoriesList: FC<PropsWithChildren<MarketCategoriesListProps>> = ({filter_items_by_login, filter_items_by_game, default_category, children}) => {

    const login = useLoginServerAuto();
    const game = useGameServerAuto();
    const { loading, loaded, error, categories } = useMarketCategories();
    const {category_url} = useParams<{category_url?: string}>();
    
    const { items: marketItems } = useMarketItems();
    const items = useItems(marketItems.map(x => x.itemId) || []);

    const data = useMemo<InjectContextType<MarketCategory, MarketCategoriesListChildProps>[]>(() => {
        
        const filteredCategories = categories.map(category => {
            
            const categoryMarketItems = marketItems.filter(item => item.categories.includes(category.id));
            const categoryItems = items.filter(item => {
                
                if(!filter_items_by_login && !filter_items_by_game)
                    return true;

                if (filter_items_by_login && login && !checkItemServers(item, login.id)) return false;
                if (filter_items_by_game && game && !checkItemServers(item, game.id)) return false;
                
                return categoryMarketItems.some(marketItem => marketItem.itemId === item.id);
            });

            return {
                ...category,
                itemsCount: categoryItems.length
            };
        }).filter(x => x.itemsCount > 0);
        
        return filteredCategories.map((category) => ({
            key: category.id,
            value: category,
            childData: {
                market_category_has_icon: Boolean(category.icon),
                market_category_active: filteredCategories.length === 1 || (category_url ? category.shortName === category_url : category.shortName === default_category),
                market_category_id: category.id,
                market_category_url: category.shortName,
                market_category_error: Boolean(error),
                market_category_loaded: loaded,
                market_category_loading: loading,
                market_category_items_count: category.itemsCount
            }
        }));
    }, [categories, category_url, default_category, error, filter_items_by_game, filter_items_by_login, game, items, loaded, loading, login, marketItems]);

    return injectContextArray(data, MarketCategoryProvider, children);
}

export default MarketCategoriesList;